/**
 * PC-v2.2-C2.3-D1 — «INFORME DE GESTIÓN (EXCEL)» (E6) EN CALIDAD → INDICADORES
 *
 * Un botón junto a los filtros de la pestaña que descarga el informe de gestión operativa interna: operación (OT y
 * tareas) y calidad (incidencias, NC y acciones) de UN cliente, de uno o todos sus centros autorizados, en el periodo de
 * la pestaña. Qué cifras salen y sobre qué centros lo decide el servidor (permisos y ámbito; el ENCARGADO, sus centros):
 * el navegador sólo repite la selección de la pestaña, sin tocarla.
 *
 *   · Cliente: el del centro elegido; sin centro, el del filtro «Cliente»; si la sesión sólo alcanza uno, ése. Con
 *     varios y ninguno elegido, el botón espera y lo dice (el informe es de un cliente cada vez).
 *   · Periodo: el de la pestaña, hasta 366 días (si es más largo, se dice; la pestaña sigue igual).
 *   · Se ve el ámbito efectivo antes de descargar (cliente, centro o todos, periodo).
 *   · Sólo se pinta con las cinco llaves (exportar + leer OT, incidencias, NC e indicadores); el servidor las vuelve a
 *     exigir (403) y nunca lo sirve a un CLIENTE.
 */
import type { ReactNode } from 'react';

import { fecha } from './base.tsx';
import { BotonExportarExcel } from './exportar-excel.tsx';
import { errorDelRango, type Rango } from '../periodo-analitico.ts';

export const RUTA_INFORME_GESTION = '/api/informes/gestion/exportar';
export const MAX_DIAS_GESTION = 366;
const LLAVES = ['informes_operativos:exportar', 'ots:leer', 'incidencias:leer', 'nc:leer', 'calidad:indicadores'] as const;

export function puedeExportarGestion(puede: (p: string) => boolean): boolean {
  return LLAVES.every((p) => puede(p));
}

export interface CentroGestion { readonly id: string; readonly descripcion: string; readonly cliente: string; readonly idCliente?: string }

/** El cliente del informe con la selección de la pestaña, o '' si todavía no hay uno. */
export function clienteDelInforme(centros: readonly CentroGestion[], idCliente: string, idCentro: string): string {
  if (idCentro !== '') return centros.find((c) => c.id === idCentro)?.idCliente ?? '';
  if (idCliente !== '') return idCliente;
  const unicos = new Set(centros.flatMap((c) => (c.idCliente === undefined ? [] : [c.idCliente])));
  return unicos.size === 1 ? [...unicos][0]! : '';
}

export function ExportarInformeGestion({ puede, centros, cargando, idCliente, idCentro, rango }: {
  puede: (p: string) => boolean;
  /** Los centros que alcanza la sesión (`/api/calidad/centros`), con su cliente. */
  centros: readonly CentroGestion[];
  cargando: boolean;
  idCliente: string;
  idCentro: string;
  rango: Rango;
}): ReactNode {
  if (!puedeExportarGestion(puede)) return null;
  const cliente = clienteDelInforme(centros, idCliente, idCentro);
  const errorPeriodo = errorDelRango(rango, MAX_DIAS_GESTION);
  //  El periodo de la pestaña puede ser más largo (sus indicadores admiten tres años): el informe, no.
  const motivo = errorPeriodo !== null ? `Informe de gestión: ${errorPeriodo}`
    : cliente === '' ? (cargando ? 'Cargando los clientes…' : 'Elige un cliente en el filtro «Cliente»: el informe de gestión es de un cliente cada vez.')
      : null;
  const ruta = motivo !== null ? null
    : `${RUTA_INFORME_GESTION}?${new URLSearchParams({ idCliente: cliente, desde: rango.desde, hasta: rango.hasta, ...(idCentro === '' ? {} : { idCentro }) }).toString()}`;
  const nombreCliente = centros.find((c) => c.idCliente === cliente)?.cliente ?? '';
  const centro = centros.find((c) => c.id === idCentro);
  const delCliente = centros.filter((c) => c.idCliente === cliente).length;
  const ambito = cliente === '' ? null
    : `${nombreCliente} · ${centro === undefined ? `todos sus centros autorizados (${String(delCliente)})` : centro.descripcion}`
      + (errorPeriodo === null ? ` · del ${fecha(rango.desde)} al ${fecha(rango.hasta)}` : '');
  return (
    <div className="informe-gestion" style={{ flexBasis: '100%' }}>
      <div className="fila" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <BotonExportarExcel
          ruta={ruta} motivoDeshabilitado={motivo} etiqueta="Informe de gestión (Excel)"
          ayuda="Informe interno de operación y calidad del cliente, con los centros y el periodo elegidos"
        />
        {ambito !== null && <span className="pequena tenue" data-testid="ambito-gestion">Uso interno · {ambito}</span>}
      </div>
      {motivo !== null && motivo !== 'Cargando los clientes…' && <p className="pequena" role="alert" style={{ margin: '4px 0 0' }}>{motivo}</p>}
    </div>
  );
}
